// www/src/components/ServicesManager.js
import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../config';
import ServiceEditor from './ServiceEditor';
import AddonEditor from './AddonEditor';
import ServicesPageHeaderEditor from './ServicesPageHeaderEditor';
import './ServicesManager.css';

export default function ServicesManager() {
  const { authenticatedFetch } = useAuth();
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [editing, setEditing] = useState(null);
  const [editingAddon, setEditingAddon] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [servicesRes, addonsRes] = await Promise.all([
      authenticatedFetch(`${API_BASE_URL}/api/services/admin/all`),
      authenticatedFetch(`${API_BASE_URL}/api/addons/admin/all`),
    ]);
    setServices(servicesRes.ok ? await servicesRes.json() : []);
    setAddons(addonsRes.ok ? await addonsRes.json() : []);
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function onDeleteService(id) {
    if (!window.confirm('Delete this service?')) return;
    await authenticatedFetch(`${API_BASE_URL}/api/services/${id}`, { method: 'DELETE' });
    load();
  }

  async function onDeleteAddon(id) {
    if (!window.confirm('Delete this add-on?')) return;
    await authenticatedFetch(`${API_BASE_URL}/api/addons/${id}`, { method: 'DELETE' });
    load();
  }

  if (editing !== null) {
    return (
      <ServiceEditor
        service={editing === 'new' ? null : editing}
        onDone={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  if (editingAddon !== null) {
    return (
      <AddonEditor
        addon={editingAddon === 'new' ? null : editingAddon}
        onDone={() => { setEditingAddon(null); load(); }}
        onCancel={() => setEditingAddon(null)}
      />
    );
  }

  return (
    <div className="services-manager">
      <ServicesPageHeaderEditor />

      <div className="services-manager-head">
        <h2>Services</h2>
        <button className="btn-primary" onClick={() => setEditing('new')}>+ Add Service</button>
      </div>
      {loading ? <p>Loading...</p> : (
        <table className="services-table">
          <thead>
            <tr><th>Order</th><th>Name</th><th>Published</th><th>Starting at</th><th></th></tr>
          </thead>
          <tbody>
            {services.map((s) => {
              const startPrice = s.pricing?.length > 0
                ? Math.min(...s.pricing.map((p) => Number(p.price))).toFixed(2)
                : '—';
              return (
                <tr key={s.id}>
                  <td>{s.sortOrder}</td>
                  <td>{s.name}</td>
                  <td>{s.published ? 'Yes' : 'No'}</td>
                  <td>${startPrice}</td>
                  <td>
                    <button className="btn-secondary" onClick={() => setEditing(s)}>Edit</button>
                    <button className="btn-danger" onClick={() => onDeleteService(s.id)}>Delete</button>
                  </td>
                </tr>
              );
            })}
            {services.length === 0 && <tr><td colSpan="5" style={{ color: 'var(--color-text-muted)' }}>No services yet.</td></tr>}
          </tbody>
        </table>
      )}

      <div className="services-manager-head" style={{ marginTop: 32 }}>
        <h2>Add-ons</h2>
        <button className="btn-primary" onClick={() => setEditingAddon('new')}>+ Add Add-on</button>
      </div>
      {loading ? null : (
        <table className="services-table">
          <thead>
            <tr><th>Order</th><th>Name</th><th>+ Price</th><th>+ Duration</th><th>Published</th><th></th></tr>
          </thead>
          <tbody>
            {addons.map((a) => (
              <tr key={a.id}>
                <td>{a.sortOrder}</td>
                <td>{a.name}</td>
                <td>${Number(a.priceDelta).toFixed(2)}</td>
                <td>{a.durationDelta} min</td>
                <td>{a.published ? 'Yes' : 'No'}</td>
                <td>
                  <button className="btn-secondary" onClick={() => setEditingAddon(a)}>Edit</button>
                  <button className="btn-danger" onClick={() => onDeleteAddon(a.id)}>Delete</button>
                </td>
              </tr>
            ))}
            {addons.length === 0 && <tr><td colSpan="6" style={{ color: 'var(--color-text-muted)' }}>No add-ons yet.</td></tr>}
          </tbody>
        </table>
      )}
    </div>
  );
}
